// Central hub for role-specific behavior and configuration.
// Expand this module when new role-driven rules appear (permissions, UI tweaks, etc.).

import {
  MANUAL_LINK,
  MANUAL_LINK_PARTNERS,
  FEEDBACK_LINK,
  FEEDBACK_LINK_PARTNERS,
} from "data/constants";
import { USER_ROLES } from "data/user";
import { can } from "data/permissions";

// Treat EXTERNAL the same as PARTNER across the app.
export function isPartnerLike(role) {
  return role === USER_ROLES.PARTNER || role === USER_ROLES.EXTERNAL;
}

/**
 * Manual link overrides by role.
 * Keep keys alphabetically sorted for readability.
 */
export const MANUAL_LINKS_BY_ROLE = {
  [USER_ROLES.PARTNER]: MANUAL_LINK_PARTNERS,
  [USER_ROLES.EXTERNAL]: MANUAL_LINK_PARTNERS,
  // [USER_ROLES.ADMIN]: "https://example.com/admin-manual", // ← example placeholder
};

/**
 * Returns the correct manual link for the given user role.
 * Falls back to the default MANUAL_LINK when no override is defined.
 *
 * @param {string | undefined} role – current user role (e.g. "partner", "admin").
 * @returns {string} URL to the manual suitable for this role.
 */
export function getManualLink(role) {
  return MANUAL_LINKS_BY_ROLE[role ?? ""] ?? MANUAL_LINK;
}

/**
 * Returns channel objects visible to the given role.
 * Admins can see every enabled channel, while other roles only see channels
 * that are not explicitly hidden (i.e. \`visible !== false\`).
 *
 * @param {Array<{visible?: boolean}>} channels – raw CHANNELS array.
 * @param {string} role – current user role.
 * @param {{livestream?: boolean}} featureFlags – environment channel switches.
 * @returns {Array} filtered list of channels.
 */
export function getVisibleChannels(channels, role, featureFlags) {
  if (!Array.isArray(channels)) {
    throw new TypeError("channels must be an array");
  }

  const enabledChannels = channels.filter(
    (ch) => ch.value !== "livestream" || featureFlags?.livestream === true
  );

  // Admins and superadmins can see every channel.
  if (can(role, "viewAllChannels")) return enabledChannels;

  // Partners (and partner-like roles) only see the dedicated "Partners" channel,
  // even if it is hidden by default.
  if (isPartnerLike(role)) {
    return enabledChannels.filter((ch) => ch.value === "partners");
  }

  // All other roles see channels that are not explicitly hidden.
  return enabledChannels.filter((ch) => ch.visible !== false);
}

export function isChannelPathHidden(pathname, featureFlags) {
  return /^\/livestream(?:\/|$)/.test(pathname) && featureFlags?.livestream !== true;
}

/**
 * Returns the default channel object for the given role.
 * For non-admin roles this is simply the first visible channel.
 *
 * @param {Array<{label: string, value: string, visible?: boolean}>} channels
 * @param {string} role
 * @param {{livestream?: boolean}} featureFlags
 * @returns {{label: string, value: string}}
 */
export function getDefaultChannelForRole(channels, role, featureFlags) {
  const visible = getVisibleChannels(channels, role, featureFlags);
  if (!visible.length) {
    throw new Error(`No visible channels for role=${role}`);
  }
  const first = visible[0];
  return {label: first.label, value: first.value};
}

// -----------------------------------------------------------------------------
// Feedback link logic
// -----------------------------------------------------------------------------

export const FEEDBACK_LINKS_BY_ROLE = {
  [USER_ROLES.PARTNER]: FEEDBACK_LINK_PARTNERS,
  [USER_ROLES.EXTERNAL]: FEEDBACK_LINK_PARTNERS,
  // [USER_ROLES.ADMIN]: "https://example.com/admin-feedback", // ← placeholder
};

export function getFeedbackLink(role) {
  return FEEDBACK_LINKS_BY_ROLE[role ?? ""] ?? FEEDBACK_LINK;
}

// -----------------------------------------------------------------------------
// Future role-specific helpers can be added below, for example:
// -----------------------------------------------------------------------------

/**
 * Example: does the role have access to advanced export features?
 *
 * @param {string} role
 * @returns {boolean}
 */
// export function canUseAdvancedExport(role) {
//   return [USER_ROLES.ADMIN, USER_ROLES.INTERNAL].includes(role);
// }
